import type { ReactNode } from 'react';
import { z } from 'zod';
import type { AeroCandidateReviewRow } from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';

interface ExitConfigChipsProps {
    candidate: Pick<AeroCandidateReviewRow, 'risk' | 'trail' | 'target' | 'exitLogicStructure'>;
}

interface ExitStructureCounts {
    partialExits: number;
    floors: number;
    hasTrailing: boolean;
    stopLossBasis: 'openingPremium' | 'requiredCapital';
    maxHoldBars: number | null;
}

const exitStructureSchema = z.object({
    stopLoss: z.object({ basis: z.enum(['openingPremium', 'requiredCapital']).optional(), trailing: z.unknown().optional() }).nullish(),
    profitTarget: z.object({ partialExits: z.array(z.unknown()).nullish() }).nullish(),
    profitFloor: z.unknown().optional(),
    maxHoldBars: z.number().int().positive().nullish()
});

const formatPercent = (value: number): string => {
    const rounded = Math.round(value * 100) / 100;

    return `${rounded}%`;
};

const parseExitStructure = (raw: string): ExitStructureCounts => {
    const empty: ExitStructureCounts = {
        partialExits: 0,
        floors: 0,
        hasTrailing: false,
        stopLossBasis: 'openingPremium',
        maxHoldBars: null
    };

    if (!raw || (!raw.trim().startsWith('{') && !raw.trim().startsWith('['))) return empty;

    try {
        const result = exitStructureSchema.safeParse(JSON.parse(raw));
        if (!result.success) return empty;
        const parsed = result.data;

        return {
            partialExits: Array.isArray(parsed.profitTarget?.partialExits) ? parsed.profitTarget.partialExits.length : 0,
            floors: Array.isArray(parsed.profitFloor) ? parsed.profitFloor.length : 0,
            hasTrailing: Boolean(parsed.stopLoss?.trailing),
            stopLossBasis: parsed.stopLoss?.basis === 'requiredCapital' ? 'requiredCapital' : 'openingPremium',
            maxHoldBars: parsed.maxHoldBars ?? null
        };
    } catch {
        return empty;
    }
};

const Chip = ({ tone, children }: { tone: string; children: ReactNode }) => (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{children}</span>
);

const ExitConfigChips = ({ candidate }: ExitConfigChipsProps) => {
    const structure = parseExitStructure(candidate.exitLogicStructure);

    return (
        <div className="flex flex-wrap gap-1.5">
            {candidate.risk > 0 && (
                <Chip tone="bg-red-900/50 text-red-200">
                    Stop {formatPercent(candidate.risk)}
                    {structure.stopLossBasis === 'requiredCapital' ? ' of required capital' : ''}
                </Chip>
            )}
            {structure.hasTrailing && candidate.trail > 0 && (
                <Chip tone="bg-purple-900/50 text-purple-200">Trail {formatPercent(candidate.trail)}</Chip>
            )}
            {candidate.target > 0 && <Chip tone="bg-green-900/50 text-green-200">Target {formatPercent(candidate.target)}</Chip>}
            {structure.partialExits > 0 && (
                <Chip tone="bg-cyan-900/50 text-cyan-200">
                    {structure.partialExits} partial{structure.partialExits === 1 ? '' : 's'}
                </Chip>
            )}
            {structure.floors > 0 && (
                <Chip tone="bg-amber-900/50 text-amber-200">
                    {structure.floors} floor{structure.floors === 1 ? '' : 's'}
                </Chip>
            )}
            {structure.maxHoldBars !== null && <Chip tone="bg-blue-900/50 text-blue-200">Hold {structure.maxHoldBars} bars</Chip>}
            {candidate.risk <= 0 && candidate.target <= 0 && structure.floors === 0 && structure.maxHoldBars === null && (
                <Chip tone="bg-zinc-800 text-zinc-400">No fixed exits</Chip>
            )}
        </div>
    );
};

export default ExitConfigChips;
